import { useState } from 'react';
import PageContainer from './PageContainer';
import { useAuth } from '../contexts/AuthContext';
import { LIST_ROWS_PER_PAGE, VIOLATION_TYPE_FILTERS } from '../constants';
import { useAcceptPostMutation } from '../api/use-accept-post-mutation';
import { useRejectPostMutation } from '../api/use-reject-post-mutation';
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Radio,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { ContentBox } from '../styles/StyledComponents';
import PortalPaginationControls from './layout/PortalPaginationControls';
import { PortalTableCell, PortalTableHead, PortalTableRow } from '../styles/portalListTable';

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginTop: theme.spacing(2),
  borderRadius: theme.spacing(2),
  boxShadow: 'none',
  border: `1px solid ${theme.palette.divider}`,
  maxHeight: 'calc(100vh - 360px)',
  minHeight: 0,
  overflow: 'auto',
}));

const ActionButton = styled(IconButton)(({ theme, variant }) => ({
  width: 32,
  height: 32,
  padding: theme.spacing(0.5),
  borderRadius: theme.spacing(1),
  backgroundColor: 'white',
  border: `1px solid ${variant === 'approve' ? '#4CAF50' : '#F44336'}`,
  color: variant === 'approve' ? '#4CAF50' : '#F44336',
  marginRight: theme.spacing(1),
  '&:hover': {
    backgroundColor: 'action.hover',
    borderColor: variant === 'approve' ? '#45a049' : '#d32f2f',
  },
}));

const ClickablePostOwner = styled('span')(() => ({
  cursor: 'pointer',
  color: 'inherit',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

// Constants
const REJECTION_REASONS = [
  'Content violates community guidelines',
  'Inappropriate language or behavior',
  'Spam or promotional content',
  'Copyright infringement',
];

const Posts = () => {
  const navigate = useNavigate();
  const { posts, postsError: error, postsInitialized, postsLoading, loadPosts } = useAuth();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [page, setPage] = useState(1);

  // Dialog states
  const [isApprovalDialogOpen, setIsApprovalDialogOpen] = useState(false);
  const [approvalStep, setApprovalStep] = useState(1);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [rejectionStep, setRejectionStep] = useState(1);
  const [rejectionReason, setRejectionReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [selectedPostId, setSelectedPostId] = useState(null);
  const acceptPostMutation = useAcceptPostMutation();
  const rejectPostMutation = useRejectPostMutation();
  const isListLoading = !postsInitialized || postsLoading;
  const isModerating = acceptPostMutation.isPending || rejectPostMutation.isPending;

  // Refresh posts data if needed
  const refreshPosts = () => {
    loadPosts();
  };

  // Filter and search logic
  const filteredPosts = (posts || []).filter((post) => {
    const searchLower = search.toLowerCase();
    const matchesSearch =
      post.postOwner.toLowerCase().includes(searchLower) ||
      post.violationType.toLowerCase().includes(searchLower) ||
      post.reportedBy.toLowerCase().includes(searchLower) ||
      (post.contentText && post.contentText.toLowerCase().includes(searchLower));

    // Handle violation type filter - post.violationType could be a string or array
    const matchesFilter =
      filter === '' ||
      (() => {
        const violationTypes = Array.isArray(post.originalData?.violationTypes)
          ? post.originalData.violationTypes
          : [post.violationType.toLowerCase()];
        return violationTypes.some((type) => type.toLowerCase() === filter.toLowerCase());
      })();

    return matchesSearch && matchesFilter;
  });

  // Sort the filtered posts based on sortBy value
  const sortedPosts = [...filteredPosts].sort((a, b) => {
    if (!sortBy) return 0;

    switch (sortBy) {
      case 'postOwner':
        return a.postOwner.localeCompare(b.postOwner);
      case 'violationType':
        return a.violationType.localeCompare(b.violationType);
      case 'violationDate':
        return (
          new Date(a.originalData?.violationDateTime || 0) -
          new Date(b.originalData?.violationDateTime || 0)
        );
      case 'reportedBy':
        return a.reportedBy.localeCompare(b.reportedBy);
      default:
        return 0;
    }
  });

  // Pagination logic
  const totalPages = Math.ceil(sortedPosts.length / LIST_ROWS_PER_PAGE);
  const paginatedPosts = sortedPosts.slice(
    (page - 1) * LIST_ROWS_PER_PAGE,
    page * LIST_ROWS_PER_PAGE,
  );

  const handleApprove = (postId) => {
    setSelectedPostId(postId);
    setIsApprovalDialogOpen(true);
    setApprovalStep(1);
  };

  const handleReject = (postId) => {
    setSelectedPostId(postId);
    setIsRejectDialogOpen(true);
    setRejectionStep(1);
    setRejectionReason('');
    setOtherReason('');
  };

  const handleApprovalConfirm = async () => {
    try {
      await acceptPostMutation.mutateAsync(selectedPostId);
      setApprovalStep(2);
      // Reload the moderation queue
      refreshPosts();
    } catch (error) {
      console.error('Error accepting post:', error);
    }
  };

  const handleApprovalFinal = () => {
    setIsApprovalDialogOpen(false);
    setSelectedPostId(null);
    setApprovalStep(1);
  };

  const handleRejectConfirm = async () => {
    try {
      const reason = rejectionReason === 'Other' ? otherReason : rejectionReason;
      await rejectPostMutation.mutateAsync({ postId: selectedPostId, reason });
      setRejectionStep(2);
      // Reload the moderation queue
      refreshPosts();
    } catch (error) {
      console.error('Error rejecting post:', error);
    }
  };

  const handleRejectFinal = () => {
    setIsRejectDialogOpen(false);
    setSelectedPostId(null);
    setRejectionStep(1);
    setRejectionReason('');
    setOtherReason('');
  };

  const handleDialogClose = () => {
    setIsApprovalDialogOpen(false);
    setIsRejectDialogOpen(false);
    setSelectedPostId(null);
    setApprovalStep(1);
    setRejectionStep(1);
    setRejectionReason('');
    setOtherReason('');
  };

  const handleReasonChange = (event) => {
    setRejectionReason(event.target.value);
    if (event.target.value !== 'Other') {
      setOtherReason('');
    }
  };

  const handleOtherReasonChange = (event) => {
    setOtherReason(event.target.value);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(1);
  };

  const handleFilterChange = (event) => {
    setFilter(event.target.value);
    setPage(1);
  };

  const handleSortChange = (event) => {
    setSortBy(event.target.value);
  };

  const handlePostOwnerClick = (postId) => {
    // Find the post to check if it's a message
    const item = posts.find((post) => post.id === postId);
    const isMessage =
      item?.originalData?.violationTypes?.includes('message') ||
      item?.originalData?.moderationType === 'chat_message' ||
      item?.originalData?.content?.type === 'chat_message';

    if (isMessage) {
      navigate(`/message-details/${postId}`);
    } else {
      navigate(`/post-details/${postId}`);
    }
  };

  return (
    <PageContainer title="Posts Under Review">
      <ContentBox>
        {/* Search, Filter, and Sort Controls */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search"
                value={search}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Violation Type</InputLabel>
                <Select
                  value={filter}
                  onChange={handleFilterChange}
                  label="Filter by Violation Type"
                >
                  {VIOLATION_TYPE_FILTERS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort by</InputLabel>
                <Select value={sortBy} onChange={handleSortChange} label="Sort by">
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="postOwner">Post Owner</MenuItem>
                  <MenuItem value="violationType">Violation Type</MenuItem>
                  <MenuItem value="violationDate">Violation Date</MenuItem>
                  <MenuItem value="reportedBy">Reported By</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        {/* Posts Table */}
        <StyledTableContainer component={Paper}>
          {isListLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress color="primary" />
            </Box>
          ) : error ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
              <Typography color="error">Error: {error}</Typography>
              <Button onClick={refreshPosts} variant="outlined" sx={{ mt: 2 }}>
                Retry
              </Button>
            </Box>
          ) : !posts || posts.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <Typography>No posts found</Typography>
            </Box>
          ) : (
            <Table stickyHeader>
              <PortalTableHead>
                <TableRow>
                  <TableCell>Post Owner</TableCell>
                  <TableCell>Violation Type</TableCell>
                  <TableCell>Content</TableCell>
                  <TableCell>Violation Date</TableCell>
                  <TableCell>Reported By</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </PortalTableHead>
              <TableBody>
                {paginatedPosts.map((post) => {
                  // Truncate content text for display (max 100 chars)
                  const contentDisplay = post.contentText
                    ? post.contentText.length > 100
                      ? `${post.contentText.substring(0, 100)}...`
                      : post.contentText
                    : 'No content available';

                  return (
                    <PortalTableRow key={post.id}>
                      <PortalTableCell>
                        <ClickablePostOwner onClick={() => handlePostOwnerClick(post.id)}>
                          {post.postOwner}
                        </ClickablePostOwner>
                      </PortalTableCell>
                      <PortalTableCell>{post.violationType}</PortalTableCell>
                      <PortalTableCell
                        sx={{
                          maxWidth: 300,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={post.contentText || 'No content available'}
                      >
                        {contentDisplay}
                      </PortalTableCell>
                      <PortalTableCell>{post.violationDate}</PortalTableCell>
                      <PortalTableCell>{post.reportedBy}</PortalTableCell>
                      <PortalTableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <ActionButton
                            variant="approve"
                            disabled={isModerating || isListLoading}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleApprove(post.id);
                            }}
                          >
                            <CheckIcon fontSize="small" />
                          </ActionButton>
                          <ActionButton
                            variant="reject"
                            disabled={isModerating || isListLoading}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReject(post.id);
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </ActionButton>
                        </Box>
                      </PortalTableCell>
                    </PortalTableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </StyledTableContainer>

        {/* Pagination */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <PortalPaginationControls
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </Box>

        {/* Approval Dialog - Step 1: Confirmation Question */}
        <Dialog
          open={isApprovalDialogOpen && approvalStep === 1}
          onClose={handleDialogClose}
          PaperProps={{
            style: {
              borderRadius: 16,
              padding: '16px',
              textAlign: 'center',
              maxWidth: '400px',
            },
          }}
        >
          <DialogContent sx={{ pb: 2 }}>
            <Box
              sx={{
                p: 2,
                width: 60,
                height: 60,
                borderRadius: '50%',
                backgroundColor: 'warning.main',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '0 auto',
              }}
            >
              <CheckIcon sx={{ fontSize: 40, color: 'white' }} />
            </Box>
            <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
              Are you sure you want to approve this post?
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
              {selectedPostId}
            </Typography>
          </DialogContent>
          <DialogActions
            sx={{
              justifyContent: 'center',
              pt: 0,
              gap: 1.5,
              px: 3,
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <Button
              onClick={handleDialogClose}
              variant="outlined"
              disabled={acceptPostMutation.isPending}
              sx={{
                color: 'warning.main',
                borderColor: 'warning.main',
                '&:hover': {
                  backgroundColor: 'warning.light',
                  borderColor: 'warning.main',
                },
                width: { xs: '100%', sm: 'auto' },
                minWidth: { sm: 120 },
                height: 48,
              }}
            >
              Cancel
            </Button>
            <LoadingButton
              onClick={handleApprovalConfirm}
              variant="contained"
              loading={acceptPostMutation.isPending}
              disabled={acceptPostMutation.isPending}
              sx={{
                backgroundColor: 'warning.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'warning.dark',
                },
                width: { xs: '100%', sm: 'auto' },
                minWidth: { sm: 120 },
                height: 48,
              }}
            >
              Approve
            </LoadingButton>
          </DialogActions>
        </Dialog>

        {/* Approval Dialog - Step 2: Success Confirmation */}
        <Dialog
          open={isApprovalDialogOpen && approvalStep === 2}
          onClose={handleDialogClose}
          PaperProps={{
            style: {
              borderRadius: 16,
              padding: '16px',
              textAlign: 'center',
              maxWidth: '400px',
            },
          }}
        >
          <DialogContent sx={{ pb: 2 }}>
            <Box
              sx={{
                p: 2,
                width: 60,
                height: 60,
                borderRadius: '50%',
                backgroundColor: 'success.main',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '0 auto',
              }}
            >
              <CheckIcon sx={{ fontSize: 40, color: 'white' }} />
            </Box>
            <Typography variant="body2" sx={{ mt: 3, mb: 1 }}>
              You have successfully approved the post
            </Typography>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
              {selectedPostId}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pt: 0, px: 3 }}>
            <Button
              onClick={handleApprovalFinal}
              variant="contained"
              sx={{
                backgroundColor: 'warning.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'warning.dark',
                },
                width: { xs: '100%', sm: 'auto' },
                minWidth: { sm: 300 },
                height: 48,
              }}
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reject Dialog - Step 1: Reason Selection */}
        <Dialog
          open={isRejectDialogOpen && rejectionStep === 1}
          onClose={handleDialogClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ textAlign: 'left', fontWeight: 'bold' }}>Reject Post</DialogTitle>
          <DialogContent>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                textAlign: 'left',
                mb: 2,
                mt: 1,
              }}
            >
              Before you proceed, please provide a reason for rejecting this post
            </Typography>
            <RadioGroup value={rejectionReason} onChange={handleReasonChange}>
              {REJECTION_REASONS.map((text, index) => (
                <FormControlLabel
                  key={index}
                  value={text}
                  control={<Radio />}
                  label={text}
                  sx={{ mb: 2, borderBottom: '1px solid', borderBottomColor: 'divider', pb: 2 }}
                />
              ))}
              <FormControlLabel value="Other" control={<Radio />} label="Other" />
            </RadioGroup>
            {rejectionReason === 'Other' && (
              <TextField
                fullWidth
                multiline
                rows={3}
                value={otherReason}
                onChange={handleOtherReasonChange}
                placeholder="Please specify the reason"
                sx={{ mt: 2 }}
              />
            )}
          </DialogContent>
          <DialogActions
            sx={{
              justifyContent: 'center',
              gap: 1.5,
              px: 3,
              py: 3,
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <Button
              onClick={handleDialogClose}
              variant="outlined"
              disabled={rejectPostMutation.isPending}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                minWidth: { sm: 200 },
                height: 48,
                color: 'warning.main',
                borderColor: 'warning.main',
                '&:hover': {
                  backgroundColor: 'warning.light',
                  borderColor: 'warning.main',
                },
              }}
            >
              Cancel
            </Button>
            <LoadingButton
              onClick={handleRejectConfirm}
              variant="contained"
              loading={rejectPostMutation.isPending}
              disabled={
                rejectPostMutation.isPending ||
                !rejectionReason ||
                (rejectionReason === 'Other' && !otherReason)
              }
              sx={{
                width: { xs: '100%', sm: 'auto' },
                minWidth: { sm: 200 },
                height: 48,
                backgroundColor: 'warning.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'warning.dark',
                },
                '&.Mui-disabled': {
                  backgroundColor: 'action.disabledBackground',
                  color: 'action.disabled',
                },
              }}
            >
              Reject
            </LoadingButton>
          </DialogActions>
        </Dialog>

        {/* Reject Dialog - Step 2: Confirmation */}
        <Dialog
          open={isRejectDialogOpen && rejectionStep === 2}
          onClose={handleDialogClose}
          PaperProps={{
            style: {
              borderRadius: 16,
              padding: '24px',
              textAlign: 'center',
              maxWidth: '400px',
            },
          }}
        >
          <DialogContent sx={{ pb: 2 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                backgroundColor: 'error.main',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '0 auto 24px',
              }}
            >
              <CloseIcon sx={{ fontSize: 40, color: 'white' }} />
            </Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              You have rejected the post
            </Typography>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
              {selectedPostId}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pt: 0, px: 3 }}>
            <Button
              onClick={handleRejectFinal}
              variant="contained"
              sx={{
                width: { xs: '100%', sm: 'auto' },
                minWidth: { sm: 200 },
                height: 48,
                backgroundColor: 'warning.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'warning.dark',
                },
              }}
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </ContentBox>
    </PageContainer>
  );
};

export default Posts;
